import {
  Autocomplete,
  Avatar,
  Button,
  Card,
  CardHeader,
  IconButton,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { post } from "../helpers/request";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { reloadSites } from "../helpers/common_requests";

const itemStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "white",
  borderRadius: "12px",
  boxShadow: 24,
  p: 4,
};

const StyledSelect = styled(Select)`
  margin: 10px 0px;
`;

const StyledTextField = styled(TextField)`
  margin: 10px 0px;
`;

const StyledButton = styled(Button)`
  margin: 10px 0px;
`;

const initialForm = {
  name: "",
  plan: "",
  unit_type: "",
};

const renderInputStyle = { marginTop: 2, marginBottom: 2 }

const SwitchSiteModal = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState(initialForm);
  const [openCreateSite, setOpenCreateSite] = useState(false);
  const sites = useSelector((state) => state.sites || []);
  const open = useSelector((state) => state.switchSiteModalOpen || false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    dispatch({ type: "show-switch-site", open: false });
  }, [dispatch]);

  const handleSelection = useCallback(
    (_event, site) => {
      if (site.type === "create") {
        setOpenCreateSite(true);
      } else {
        navigate(`/${site?.site_id}`);
        handleClose();
      }
    },
    [navigate, handleClose]
  );

  const handleChangeCreateForm = ({ target }) => {
    setCreateForm((prev) => ({ ...prev, [target.name]: target.value }));
  };

  const handleGetOptionLabel = useCallback((site) => site.type === "create" ? site.text : `${site.domain || "<Sin dominio>"} - ${site.name}`, []);
  const handleRenderInput = useCallback((params) => (<TextField {...params} label="Search..." autoFocus sx={renderInputStyle} />), []);

  useEffect(() => {
    reloadSites({ dispatch })
  }, [dispatch]);

  useEffect(() => {
    if (!openCreateSite) {
      setCreateForm(initialForm);
    }
  }, [openCreateSite]);

  const enabledCreateSiteButton = useMemo(() => {
    return (
      !isCreating &&
      !!createForm.name &&
      !!createForm.plan &&
      (!!createForm.unit_type || createForm.plan !== "entrepreneur")
    );
  }, [createForm, isCreating]);

  const handleCreateSite = () => {
    setIsCreating(true);
    post(`${process.env.REACT_APP_API_URL}/internal/admin/v3/sites`, {
      name: createForm.name,
      plan: createForm.plan,
      unit_type:
        createForm?.plan === "entrepreneur" ? createForm.unit_type : undefined,
      roles: [],
    })
      .then(site_id => reloadSites({ dispatch })
        .then(() => {
          navigate(`/${site_id}`);
          handleClose();
          setIsCreating(false);
          setCreateForm(initialForm);    
        })
      )
      .catch(() => {
        setIsCreating(false);
      });
  };

  const options = useMemo(() => [{ type: "create", text: "Crear sitio" }, ...sites], [sites])

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={itemStyle}>
        {openCreateSite ? (
          <>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setOpenCreateSite(false)}
              aria-label="close"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Crear sitio</Typography>
            <StyledTextField
              placeholder="Nombre"
              fullWidth
              name="name"
              value={createForm.name}
              onChange={handleChangeCreateForm}
            />
            <StyledSelect
              value={createForm?.plan || ""}
              name="plan"
              onChange={handleChangeCreateForm}
              fullWidth
              displayEmpty
            >
              <MenuItem value="">
                <ListItemText primary={`Plan`} />
              </MenuItem>
              <MenuItem value="enterprise">
                <ListItemText primary={`Enterprise`} />
              </MenuItem>
              <MenuItem value="growth">
                <ListItemText primary={`Growth`} />
              </MenuItem>
              <MenuItem value="entrepreneur">
                <ListItemText primary={`Entrepreneur`} />
              </MenuItem>
            </StyledSelect>
            {createForm?.plan === "entrepreneur" && (
              <StyledSelect
                value={createForm?.unit_type || ""}
                name="unit_type"
                onChange={handleChangeCreateForm}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">
                  <ListItemText primary={`Tipo de unidad`} />
                </MenuItem>
                <MenuItem value="lots">
                  <ListItemText primary={`Lotes`} />
                </MenuItem>
                <MenuItem value="departments_houses">
                  <ListItemText primary={`Departamentos o casas`} />
                </MenuItem>
              </StyledSelect>
            )}
            <StyledButton
              onClick={handleCreateSite}
              disabled={!enabledCreateSiteButton}
              fullWidth
              variant="contained"
            >
              Crear sitio
            </StyledButton>
          </>
        ) : (
          <>
            <Typography variant="h6">Sitios</Typography>
            <Autocomplete
              autoHighlight
              autoComplete
              onChange={handleSelection}
              options={options}
              getOptionLabel={handleGetOptionLabel}
              renderOption={Item}
              renderInput={handleRenderInput}
            />
          </>
        )}
      </Box>
    </Modal>
  );
};

const renderItemStyle = { maxWidth: 345, cursor: 'pointer' }

const Item = (props, option) => {
  return (
    <Card sx={renderItemStyle} {...props}>
      <CardHeader
        avatar={
          option.type === "create" ? (
            <Avatar>+</Avatar>
          ) : (
            <Avatar>
              {((option.name && option.name[0]) || "X").toUpperCase()}
            </Avatar>
          )
        }
        title={option.name}
        subheader={
          option.type === "create"
            ? option.text
            : option.domain || "<sin dominio>"
        }
      />
    </Card>
  );
};

export default SwitchSiteModal;
